import axiosInstance from "../../utils/AxiosInstance";
import { toast } from "react-toastify";
import { useAuthHeader } from "react-auth-kit";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useGetPatients } from "../PatientComponents";
import { toastError, toastSuccess } from "../../utils/utils";
import { useGetOrganizations } from "../HandlingComponents";

export const thresholdsSkeleton = {
  spO2_min: "Saturazione O2 minima",
  spO2_max: "Saturazione O2 massima",
  pulse_min: "Pulsazione minima",
  pulse_max: "Pulsazione massima",
  press_sis_min: "Pressione sistolica minima",
  press_sis_max: "Pressione sistolica massima",
  press_dia_min: "Pressione diastolica minima",
  press_dia_max: "Pressione diastolica massima",
  temp_min: "Temperatura minima",
  temp_max: "Temperatura massima",
  gluc_min: "Glicemia minima",
  gluc_max: "Glicemia massima",
  email_alert: "Notifica via email",
  sms_alert: "Notifica via sms",
  cr_alert: "Notifica via CR",
};

export const services = [
  { label: "servizio_1gg", id: 1 },
  { label: "servizio_2gg", id: 2 },
  { label: "servizio_3gg", id: 3 },
  { label: "servizio_4gg", id: 4 },
  { label: "servizio_5gg", id: 5 },
  { label: "servizio_6gg", id: 6 },
  { label: "servizio_7gg", id: 7 },
  { label: "servizio_14gg", id: 8 },
  { label: "servizio_30gg", id: 9 },
  { label: "servizio_180gg", id: 10 },
  { label: "servizio_360gg", id: 11 },
  { label: "patches_glicemia", id: 12 },
  { label: "patches_temperatura", id: 13 },
  { label: "patches_spo2", id: 14 },
  { label: "patches_ecg", id: 15 },
];

// === GET /services
export const useGetServices = (cr_active) => {
  // default: cr_active è false quando viene creato, poi diventa true quando lo associo
  const authHeader = useAuthHeader();
  const auth = {
    headers: { Authorization: authHeader() },
  };
  const patientsQuery = useGetPatients();

  return useQuery({
    queryKey: cr_active ? ["services"] : ["services", "cr_not_active"],
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/services${cr_active ? "" : "?cr_active=false"}`,
        auth
      );
      let data = res.data;

      // operations on data
      let _services = [];
      for (let s of data.services) {
        const servicePatient = patientsQuery.data.patients.find(
          (el) => el.id === s.patient_id
        );

        const patientInfo = servicePatient
          ? `${servicePatient.first_name || ""} ${servicePatient.last_name || ""}`.trim()
          : "";

        const doctor = servicePatient?.doctor;
        const doctorInfo = doctor
          ? `${doctor.first_name || ""} ${doctor.last_name || ""}`.trim()
          : "";

        const org_name = servicePatient?.org_name || "";

        _services.push({
          ...s,
          patient_info: patientInfo,
          doctor_info: doctorInfo,
          org_name,
        });
      }
      data["services"] = _services;
      return data;
    },
    enabled: patientsQuery.isSuccess,
  });
};

// === DELETE /services/:service_id
export const useDeleteService = () => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (service_id) => {
      const res = await axiosInstance.delete(`/services/${service_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["services"]);
    },
  });
};

// === GET /services/patient/:patient_id
export const useGetPatientServices = (patient_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["services", "patient", patient_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/services/patient/${patient_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    enabled: patient_id !== undefined && patient_id !== null,
  });
};

// === GET /services/:service_id
export const useGetServiceById = (service_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["services", service_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/services/${service_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    enabled: service_id !== undefined && service_id !== null,
  });
};

// === PUT /services/:service_id ===
export const usePutServiceById = () => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (editedService) => {
      if (editedService === undefined) return;
      const service_id = editedService.id;
      const res = await axiosInstance.put(
        `/services/${service_id}`,
        editedService,
        {
          headers: { Authorization: authHeader() },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables, context) => {
      // close modal and display toast
      toast.success("Dettagli telemonitoraggio aggiornati!", toastSuccess);

      queryClient.invalidateQueries(["services", variables.patient_id]); // update views inside ViewDoctor
      queryClient.invalidateQueries(["services"]); // update views inside ViewDoctor
    },
    onError: (data, variables, context) => {
      // close modal and display error
      toast.error("Dettagli telemonitoraggio non aggiornati", toastError);
    },
  });
};

// === POST /services
export const useNewService = (closeModal) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newService) => {
      const res = await axiosInstance.post(`/services`, newService, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["services"]);
      toast.success("Telemonitoraggio aggiunto con successo!", toastSuccess);
      closeModal();
    },
    onError: () => {
      toast.error("Aggiunta telemonitoraggio non riuscita!", toastError);
      closeModal();
    },
  });
};

// === GET /services/:service_id
export const useGetThresholds = (service_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["services", service_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/services/${service_id}`, {
        headers: { Authorization: authHeader() },
      });
      let data = res.data;

      // operations on data
      if (data.threashold_id === null && data.threashold === undefined) {
        data["isNewThresholds"] = true;
        let tmpValue = Object.fromEntries(
          Object.keys(thresholdsSkeleton).map((key) => [key, null])
        );
        data["threashold"] = tmpValue;
      } else {
        delete data.threashold.id; // not needed, messes up map
      }

      return data;
    },
  });
};

// === PUT or POST /services/:service_id
export const useUpdateThresholds = (service_id, closeModal) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();
  const getOriginalThresholds = useGetThresholds(service_id);

  return useMutation({
    mutationFn: async (editedObject) => {
      if (editedObject === undefined) return;
      const id = editedObject.id;
      const thresholds = editedObject.threashold;
      const isNew = editedObject.isNewThresholds;

      let res;
      if (isNew)
        res = await axiosInstance.post(
          `/services/${id}/threashold`,
          thresholds,
          {
            headers: { Authorization: authHeader() },
          }
        );
      else
        res = await axiosInstance.put(
          `/services/${id}/threashold`,
          thresholds,
          {
            headers: { Authorization: authHeader() },
          }
        );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["services"]);
      queryClient.invalidateQueries(["services", variables.id]);
      toast.success("Dettagli soglie aggiornati!", toastSuccess);
      closeModal();
    },
    onError: () => {
      queryClient.setQueryData(["services", service_id], (oldData) => {
        return {
          ...oldData,
          threashold: getOriginalThresholds.data.threashold,
        };
      });
      toast.error("Dettagli soglie non aggiornati", toastError);
      closeModal();
    },
  });
};
